import { cloneDeep } from 'lodash';
import { saveProduct } from '@/api/product';
import { saveDesignTemplate } from '@/api/admin/designTemplates';
import { SaveAs } from '@/typings/designStudio/saveAs';
import { createGroupIndex, normalizeGroupIdForType } from './groups/groupSyncUtils';
(function () {
    'ngInject';

    angular.module('app.shared.services').factory('DesignStudioSaveService', designStudioSaveService);

    function designStudioSaveService(ProductService, MultipleFontService, $mdDialog, $rootScope) {
        /**
         * @param	{Object} 	saveAs An Enum that can be Template or DesignTemplate
         * @return 	{Object}    Returns the Template/DesignTemplate saved
         */
        async function processSaving(saveAs) {
            const product = ProductService.getProduct();
            const listPreview = ProductService.getListPreview();
            let groups = product.groups || [];
            const groupIndex = createGroupIndex(groups);

            if (product.groups && product.groups.length > 0) {
                product.groups.forEach((group) => {
                    group.visible = true;
                    group.locked = false;
                });
            }

            const elementsLinks = ProductService.getElementsLinks();
            const listEpsCopy = cloneDeep(product.listEPS);

            for (const eps of listEpsCopy) {
                eps.textsEps = eps.textsEps.map((text) => {
                    const {
                        fontsMap,
                        fontColorsMap,
                        color,
                        outlineColor,
                        outlineColorsMap,
                        strokeColor,
                        strokeColorsMap,
                        shadowColor,
                        shadowColorsMap
                    } = MultipleFontService.processTextBeforeSaving(text);
                    delete text.testingText;
                    delete text.sourceElement;
                    if (text.oldAllowEdit) {
                        /*Backward compatibility: If the text has the previous version of allow editing (no bounding box),
                        before saving, the allowEdit is reset to true and the rest of the properties are left as null.*/
                        text.allowEdit = true;
                        text.initCenterX = null;
                        text.initCenterY = null;
                        text.initRotation = null;
                        text.initFontSize = null;
                    }
                    delete text.oldAllowEdit;

                    const transformOptions =
                        text.transformOptions && typeof text.transformOptions !== 'string'
                            ? JSON.stringify(text.transformOptions)
                            : text.transformOptions || null;
                    const dateFormat =
                        text.dateFormat && typeof text.dateFormat !== 'string'
                            ? JSON.stringify(text.dateFormat)
                            : text.dateFormat || null;
                    text.groupId = normalizeGroupIdForType(groupIndex.byId, text.groupId, 'eps');
                    return {
                        ...text,
                        fontsMap,
                        fontColorsMap,
                        color,
                        outlineColor,
                        transformOptions,
                        dateFormat,
                        outlineColorsMap,
                        strokeColor,
                        strokeColorsMap,
                        shadowColor,
                        shadowColorsMap
                    };
                });

                eps.circularTextsEps = eps.circularTextsEps.map((circularText) => {
                    const {
                        fontsMap,
                        fontColorsMap,
                        color,
                        outlineColor,
                        outlineColorsMap
                    } = MultipleFontService.processTextBeforeSaving(circularText);
                    delete circularText.testingText;
                    circularText.groupId = normalizeGroupIdForType(groupIndex.byId, circularText.groupId, 'eps');
                    return {
                        ...circularText,
                        fontsMap,
                        fontColorsMap,
                        color,
                        outlineColor,
                        outlineColorsMap
                    };
                });

                //This should be moved to vector service
                eps.vectorEPSEps = eps.vectorEPSEps.map((vector) => {
                    delete vector.svgString;
                    if (vector.pdf) {
                        delete vector.pdf;
                    }
                    if (vector.vectorContent) {
                        delete vector.vectorContent;
                    }
                    if (!vector.starMapConfig && !vector.qrConfig) return vector;
                    const starMapConfig =
                        vector.starMapConfig && typeof vector.starMapConfig !== 'string'
                            ? JSON.stringify(vector.starMapConfig)
                            : vector.starMapConfig;
                    const qrConfig =
                        vector.qrConfig && typeof vector.qrConfig !== 'string'
                            ? JSON.stringify(vector.qrConfig)
                            : vector.qrConfig;
                    vector.groupId = normalizeGroupIdForType(groupIndex.byId, vector.groupId, 'eps');
                    return {
                        ...vector,
                        starMapConfig,
                        qrConfig
                    };
                });

                //This should be moved to image service
                eps.imagePlaceHoldersEps = eps.imagePlaceHoldersEps.map((imagePlaceHolder) => {
                    let mapConfig = null;
                    let calendarConfig = null;
                    let imageToImageConfig = null;
                    let imageEffects = null;
                    let wordCloudConfig = null;
                    let wordSearchConfig = null;
                    if (imagePlaceHolder.mapConfig) {
                        mapConfig =
                            imagePlaceHolder.mapConfig && typeof imagePlaceHolder.mapConfig !== 'string'
                                ? JSON.stringify(imagePlaceHolder.mapConfig)
                                : imagePlaceHolder.mapConfig;
                    }
                    if (imagePlaceHolder.wordCloudConfig) {
                        wordCloudConfig =
                            imagePlaceHolder.wordCloudConfig && typeof imagePlaceHolder.wordCloudConfig !== 'string'
                                ? JSON.stringify(imagePlaceHolder.wordCloudConfig)
                                : imagePlaceHolder.wordCloudConfig;
                    }
                    if (imagePlaceHolder.calendarConfig) {
                        calendarConfig =
                            imagePlaceHolder.calendarConfig && typeof imagePlaceHolder.calendarConfig !== 'string'
                                ? JSON.stringify(imagePlaceHolder.calendarConfig)
                                : imagePlaceHolder.calendarConfig;
                    }
                    if (imagePlaceHolder.wordSearchConfig) {
                        wordSearchConfig =
                            imagePlaceHolder.wordSearchConfig && typeof imagePlaceHolder.wordSearchConfig !== 'string'
                                ? JSON.stringify(imagePlaceHolder.wordSearchConfig)
                                : imagePlaceHolder.wordSearchConfig;
                    }
                    if (imagePlaceHolder.imageToImageConfig) {
                        imageToImageConfig =
                            imagePlaceHolder.imageToImageConfig &&
                            typeof imagePlaceHolder.imageToImageConfig !== 'string'
                                ? JSON.stringify(imagePlaceHolder.imageToImageConfig)
                                : imagePlaceHolder.imageToImageConfig;
                    }
                    if (imagePlaceHolder.imageEffects) {
                        imageEffects =
                            imagePlaceHolder.imageEffects && typeof imagePlaceHolder.imageEffects !== 'string'
                                ? JSON.stringify(imagePlaceHolder.imageEffects)
                                : imagePlaceHolder.imageEffects;
                    }
                    delete imagePlaceHolder.testingUrl;

                    if (imagePlaceHolder.isProductGuidelines) {
                        imagePlaceHolder.locked = true;
                        imagePlaceHolder.visible = false;
                    }
                    if (elementsLinks.some((l) => l.targetId === imagePlaceHolder.uuid && (l.valueFormat === 3 || l.valueFormat === 4))) {
                        delete imagePlaceHolder.clipPath;
                    }
                    if (!imagePlaceHolder.allowEdit) {
                        imagePlaceHolder.limitMovement = false;
                    }
                    imagePlaceHolder.groupId = normalizeGroupIdForType(groupIndex.byId, imagePlaceHolder.groupId, 'eps');
                    return {
                        ...imagePlaceHolder,
                        mapConfig,
                        wordCloudConfig,
                        calendarConfig,
                        imageToImageConfig,
                        imageEffects,
                        wordSearchConfig
                    };
                });
            }
            for (const preview of listPreview) {
                preview.textsPreview = preview.textsPreview.map((text) => {
                    if (text.clipPath && typeof text.clipPath !== 'string') {
                        try {
                            const clipPath = text.clipPath.toObject ? text.clipPath.toObject() : text.clipPath;
                            text.clipPath = JSON.stringify({
                                ...clipPath,
                                absolutePositioned: text.clipPath.absolutePositioned
                            });
                        } catch (error) {
                            console.error('Error stringifying clipPath', error);
                        }
                    }
                    const {
                        fontsMap,
                        fontColorsMap,
                        color,
                        outlineColor,
                        outlineColorsMap,
                        strokeColor,
                        strokeColorsMap,
                        shadowColor,
                        shadowColorsMap
                    } = MultipleFontService.processTextBeforeSaving(text);
                    delete text.testingText;
                    delete text.sourceElement;
                    if (text.oldAllowEdit) {
                        /*Backward compatibility: If the text has the previous version of allow editing (no bounding box),
                        before saving, the allowEdit is reset to true and the rest of the properties are left as null.*/
                        text.allowEdit = true;
                        text.initCenterX = null;
                        text.initCenterY = null;
                        text.initRotation = null;
                        text.initFontSize = null;
                    }
                    delete text.oldAllowEdit;
                    const transformOptions =
                        text.transformOptions && typeof text.transformOptions !== 'string'
                            ? JSON.stringify(text.transformOptions)
                            : text.transformOptions || null;
                    const dateFormat =
                        text.dateFormat && typeof text.dateFormat !== 'string'
                            ? JSON.stringify(text.dateFormat)
                            : text.dateFormat || null;
                    text.groupId = normalizeGroupIdForType(groupIndex.byId, text.groupId, 'preview');
                    return {
                        ...text,
                        fontsMap,
                        fontColorsMap,
                        color,
                        outlineColor,
                        transformOptions,
                        dateFormat,
                        outlineColorsMap,
                        strokeColor,
                        strokeColorsMap,
                        shadowColor,
                        shadowColorsMap
                    };
                });

                preview.circularTextsPreview = preview.circularTextsPreview.map((circularText) => {
                    const {
                        fontsMap,
                        fontColorsMap,
                        color,
                        outlineColor,
                        outlineColorsMap
                    } = MultipleFontService.processTextBeforeSaving(circularText);
                    if (circularText.clipPath && typeof circularText.clipPath !== 'string') {
                        try {
                            const clipPath = circularText.clipPath.toObject
                                ? circularText.clipPath.toObject()
                                : circularText.clipPath;
                            circularText.clipPath = JSON.stringify({
                                ...clipPath,
                                absolutePositioned: circularText.clipPath.absolutePositioned
                            });
                        } catch (error) {
                            console.error('Error stringifying clipPath', error);
                        }
                    }
                    delete circularText.testingText;
                    circularText.groupId = normalizeGroupIdForType(groupIndex.byId, circularText.groupId, 'preview');
                    return {
                        ...circularText,
                        fontsMap,
                        fontColorsMap,
                        color,
                        outlineColor,
                        outlineColorsMap
                    };
                });

                for (const imgPlaceholder of preview.imagePlaceHoldersPreview) {
                    if (imgPlaceholder.mapConfig && typeof imgPlaceholder.mapConfig !== 'string') {
                        imgPlaceholder.mapConfig = JSON.stringify(imgPlaceholder.mapConfig);
                    }
                    if (imgPlaceholder.wordCloudConfig && typeof imgPlaceholder.wordCloudConfig !== 'string') {
                        imgPlaceholder.wordCloudConfig = JSON.stringify(imgPlaceholder.wordCloudConfig);
                    }
                    if (imgPlaceholder.calendarConfig && typeof imgPlaceholder.calendarConfig !== 'string') {
                        imgPlaceholder.calendarConfig = JSON.stringify(imgPlaceholder.calendarConfig);
                    }
                    if (imgPlaceholder.imageToImageConfig && typeof imgPlaceholder.imageToImageConfig !== 'string') {
                        imgPlaceholder.imageToImageConfig = JSON.stringify(imgPlaceholder.imageToImageConfig);
                    }
                    if (imgPlaceholder.imageEffects && typeof imgPlaceholder.imageEffects !== 'string') {
                        imgPlaceholder.imageEffects = JSON.stringify(imgPlaceholder.imageEffects);
                    }
                    if (imgPlaceholder.v && typeof imgPlaceholder.v !== 'string') {
                        imgPlaceholder.v = JSON.stringify(imgPlaceholder.v);
                    }
                    if (elementsLinks.some((l) => l.targetId === imgPlaceholder.uuid && (l.valueFormat === 3 || l.valueFormat === 4))) {
                        delete imgPlaceholder.clipPath;
                    } else if (imgPlaceholder.clipPath && typeof imgPlaceholder.clipPath !== 'string') {
                        try {
                            const clipPath = imgPlaceholder.clipPath.toObject
                                ? imgPlaceholder.clipPath.toObject()
                                : imgPlaceholder.clipPath;
                            imgPlaceholder.clipPath = JSON.stringify({
                                ...clipPath,
                                absolutePositioned: imgPlaceholder.clipPath.absolutePositioned
                            });
                        } catch (error) {
                            console.error('Error stringifying clipPath', error);
                        }
                    }
                    if (ProductService.getIsProductColorModified()) imgPlaceholder.podProductImagesMetadata = null;
                    delete imgPlaceholder.testingUrl;

                    if (imgPlaceholder.isProductGuidelines) {
                        imgPlaceholder.locked = true;
                        imgPlaceholder.visible = false;
                    }
                    if (!imgPlaceholder.allowEdit) {
                        imgPlaceholder.limitMovement = false;
                    }

                    imgPlaceholder.groupId = normalizeGroupIdForType(groupIndex.byId, imgPlaceholder.groupId, 'preview');
                }

                for (const vector of preview.vectorEPSPreview) {
                    if (vector.clipPath && typeof vector.clipPath !== 'string') {
                        try {
                            const clipPath = vector.clipPath.toObject ? vector.clipPath.toObject() : vector.clipPath;
                            vector.clipPath = JSON.stringify({
                                ...clipPath,
                                absolutePositioned: vector.clipPath.absolutePositioned
                            });
                        } catch (error) {
                            console.error('Error stringifying clipPath', error);
                        }
                    }
                    if (vector.starMapConfig && typeof vector.starMapConfig !== 'string') {
                        vector.starMapConfig = JSON.stringify(vector.starMapConfig);
                    }
                    if (vector.qrConfig && typeof vector.qrConfig !== 'string') {
                        vector.qrConfig = JSON.stringify(vector.qrConfig);
                    }
                    delete vector.svgString;
                    if (vector.pdf) {
                        delete vector.pdf;
                    }
                    if (vector.vectorContent) {
                        delete vector.vectorContent;
                    }
                    vector.groupId = normalizeGroupIdForType(groupIndex.byId, vector.groupId, 'preview');
                }
            }

            try {
                switch (saveAs) {
                    case SaveAs.Template:
                        await saveProduct({ ...product, listEPS: listEpsCopy });
                        break;

                    case SaveAs.DesignTemplate:
                        await saveDesignTemplate({ ...product, listEPS: listEpsCopy }, product.id);
                }
            } catch (error) {
                let message = error.message;
                if (error.response?.status === 400) {
                    message = error.response?.data?.Message || error.message;
                }
                $mdDialog.show(
                    $mdDialog
                        .alert()
                        .parent(document.body)
                        .clickOutsideToClose(true)
                        .title('Failed to save 😢')
                        .textContent(message)
                        .ok('OK!')
                );
                console.error(error);
                throw error;
            }

            return ProductService.getProduct();
        }
        return {
            processSaving
        };
    }
})();
